import { KeyboardEventHandler, useEffect, useState } from 'react';

import { useIntersectionObserver } from '@rikstv/shared-components';

import navItemStyles from './NavItem.module.css';
import styles from './StrimNavigation.module.scss';

export const useMobileMenu = (currentUrl: string) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Close mobile menu on navigation
  useEffect(() => setMobileMenuOpen(false), [currentUrl]);

  // Close mobile menu when clicking backdrop
  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }

    const clickListener = (ev: MouseEvent) => {
      if (ev.target instanceof HTMLElement && ev.target.classList.contains(styles.mobileMenu)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', clickListener);
    return () => document.removeEventListener('click', clickListener);
  }, [mobileMenuOpen]);

  // Disable scrolling when mobile menu is open
  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [mobileMenuOpen]);

  // Explicitly "close" mobile menu when <ul> is hidden
  const [mobileNavRef] = useIntersectionObserver(entries => {
    const isIntersecting = entries.some(e => e.isIntersecting);
    if (!isIntersecting && mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  });
  return { mobileMenuOpen, setMobileMenuOpen, mobileNavRef, mobileMenuFocusTrapper };
};

const mobileMenuFocusTrapper: KeyboardEventHandler = evt => {
  if (evt.ctrlKey || evt.metaKey || evt.shiftKey) return;
  // Circles focus back to first element in menu when tabbing on last
  if (evt.key === 'Tab' && evt.target instanceof HTMLElement) {
    const lastMobileLinkSelector = `.${navItemStyles.mobileNavItem}:last-child .${navItemStyles.link}`;
    if (evt.target.matches(lastMobileLinkSelector)) {
      const logo = document.querySelector(`.${navItemStyles.strimLogo} .${navItemStyles.link}`);
      if (logo instanceof HTMLElement) {
        evt.preventDefault();
        logo.focus();
      }
    }
  }
};
